import { useEffect, useState, useCallback } from "react";
import api, { apiErr } from "@/lib/api";
import { PageHeader, Empty } from "@/components/Bits";
import { ROLE_LABELS, money } from "@/lib/format";
import { toast } from "sonner";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, MapPin, Trash2, Upload, Download } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { SuperDelete, ViewAs } from "@/components/SuperDelete";
import { SuperEdit } from "@/components/SuperEdit";
import UserEdit from "@/components/UserEdit";
import RestoreBin from "@/components/RestoreBin";
import BankHolidays from "@/components/BankHolidays";

const useRoles = () => { const { user } = useAuth(); return user.role === "management" ? ROLES : ROLES.filter((r) => r !== "management"); };

const ROLES = ["shop_floor", "supervisor", "office", "management"];

export default function Settings() {
  const { user } = useAuth();
  const superUser = user.role === "management" && !user.impersonated_by;
  return (
    <div className="p-4 sm:p-6 lg:p-8">
      <PageHeader title="Setup" subtitle="Configure staff, locations, stage templates and rules — nothing is hard-coded" />
      <Tabs defaultValue="users">
        <TabsList className="flex-wrap h-auto">
          <TabsTrigger value="users" data-testid="tab-users">People</TabsTrigger>
          <TabsTrigger value="locations" data-testid="tab-locations">Locations</TabsTrigger>
          <TabsTrigger value="templates" data-testid="tab-templates">Task List</TabsTrigger>
          <TabsTrigger value="jobtypes" data-testid="tab-jobtypes">Job Types</TabsTrigger>
          <TabsTrigger value="rules" data-testid="tab-rules">Rules</TabsTrigger>
          <TabsTrigger value="holidays" data-testid="tab-holidays">Bank Holidays</TabsTrigger>
          {superUser && <TabsTrigger value="bin" data-testid="tab-bin">Restore Bin</TabsTrigger>}
          <TabsTrigger value="overtime" data-testid="tab-overtime">Overtime & Reminders</TabsTrigger>
          <TabsTrigger value="import" data-testid="tab-import">Import CSV</TabsTrigger>
        </TabsList>
        <TabsContent value="users"><Users /></TabsContent>
        <TabsContent value="locations"><Locations /></TabsContent>
        <TabsContent value="templates"><TaskListEditor /></TabsContent>
        <TabsContent value="jobtypes"><JobTypes /></TabsContent>
        <TabsContent value="rules"><Rules /></TabsContent>
        <TabsContent value="holidays"><BankHolidays /></TabsContent>
        {superUser && <TabsContent value="bin"><RestoreBin /></TabsContent>}
        <TabsContent value="overtime"><Overtime /></TabsContent>
        <TabsContent value="import"><ImportData /></TabsContent>
      </Tabs>
    </div>
  );
}

function RoleCell({ u, roleOpts, onDone }) {
  const { user } = useAuth();
  const editable = u.id !== user.id && (user.role === "management" || u.role !== "management");
  if (!editable) return <span data-testid={`user-role-label-${u.email}`}>{ROLE_LABELS[u.role]}</span>;
  const change = async (role) => {
    if (role === u.role) return;
    try { await api.patch(`/users/${u.id}`, { role }); toast.success(`${u.name} is now ${ROLE_LABELS[role]}`); onDone(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  return (
    <Select value={u.role} onValueChange={change}>
      <SelectTrigger className="h-8 w-40" data-testid={`user-role-select-${u.email}`}><SelectValue /></SelectTrigger>
      <SelectContent>{roleOpts.map((r) => <SelectItem key={r} value={r}>{ROLE_LABELS[r]}</SelectItem>)}</SelectContent>
    </Select>
  );
}

function Users() {
  const roleOpts = useRoles();
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ name: "", email: "", role: "shop_floor", home_location: "Rathcairn", cost_rate: 0, approval_limit: 0, password: "Mck2026!" });
  const load = useCallback(() => api.get("/users").then((r) => setUsers(r.data)), []);
  const [sites, setSites] = useState(["Rathcairn", "Athboy"]);
  useEffect(() => { load(); api.get("/locations").then((r) => setSites(r.data.map((l) => l.name))); }, [load]);
  const create = async () => {
    try { await api.post("/users", f); toast.success("User created"); setOpen(false); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const toggle = async (u) => { await api.patch(`/users/${u.id}`, { active: !u.active }); load(); };
  const savePay = async (u, v) => {
    if (Number(v || 0) === Number(u.pay_rate || 0)) return;
    try { await api.patch(`/users/${u.id}`, { pay_rate: v || 0 }); toast.success(`Pay rate saved for ${u.name}`); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <>
      <div className="flex justify-end my-4">
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button data-testid="new-user-button" className="font-bold uppercase"><Plus className="w-4 h-4 mr-1" />Add Person</Button></DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle className="font-heading uppercase">Add Person</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label className="text-xs">Name</Label><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="user-name" className="mt-1" /></div>
              <div><Label className="text-xs">Email</Label><Input type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="user-email" className="mt-1" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs">Role</Label>
                  <Select value={f.role} onValueChange={(v) => setF({ ...f, role: v })}><SelectTrigger data-testid="user-role" className="mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent>{roleOpts.map((r) => <SelectItem key={r} value={r}>{ROLE_LABELS[r]}</SelectItem>)}</SelectContent></Select></div>
                <div><Label className="text-xs">Home Site</Label>
                  <Select value={f.home_location} onValueChange={(v) => setF({ ...f, home_location: v })}><SelectTrigger data-testid="user-location" className="mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="Rathcairn">Rathcairn</SelectItem><SelectItem value="Athboy">Athboy</SelectItem></SelectContent></Select></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs">Labour cost €/hr</Label><Input type="number" value={f.cost_rate} onChange={(e) => setF({ ...f, cost_rate: e.target.value })} data-testid="user-rate" className="mt-1" /></div>
                <div><Label className="text-xs">PO approval limit €</Label><Input type="number" value={f.approval_limit} onChange={(e) => setF({ ...f, approval_limit: e.target.value })} data-testid="user-approval" className="mt-1" /></div>
              </div>
              <div><Label className="text-xs">Temp password</Label><Input value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} data-testid="user-password" className="mt-1" /></div>
            </div>
            <DialogFooter><Button onClick={create} disabled={!f.name || !f.email} data-testid="user-create-submit" className="font-bold uppercase">Create</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
      <div className="card-industrial overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-accent/40 text-[11px] font-mono uppercase tracking-wider text-muted-foreground">
            <tr><th className="text-left px-4 py-3">Name</th><th className="text-left px-4 py-3">Email</th><th className="text-left px-4 py-3">Role</th>
            <th className="text-left px-4 py-3">Site</th><th className="text-right px-4 py-3">Cost €/hr</th><th className="text-right px-4 py-3">Pay €/hr</th><th className="text-right px-4 py-3">Approve €</th><th className="px-4 py-3"></th></tr>
          </thead>
          <tbody className="divide-y divide-border">
            {users.map((u) => (
              <tr key={u.id} data-testid={`user-${u.email}`} className={u.active ? "" : "opacity-50"}>
                <td className="px-4 py-3 font-semibold">{u.name}</td>
                <td className="px-4 py-3 text-muted-foreground">{u.email}</td>
                <td className="px-4 py-2"><RoleCell u={u} roleOpts={roleOpts} onDone={load} /></td>
                <td className="px-4 py-3 text-muted-foreground">{u.home_location}</td>
                <td className="px-4 py-3 text-right font-mono-num">{u.cost_rate != null ? u.cost_rate : "—"}</td>
                <td className="px-4 py-2 text-right">
                  <Input type="number" defaultValue={u.pay_rate || ""} placeholder={String(u.cost_rate ?? "")} onBlur={(e) => savePay(u, e.target.value)}
                    data-testid={`user-pay-${u.email}`} className="h-8 w-20 ml-auto text-right font-mono-num" title="Hourly pay rate for payroll (blank = cost rate)" />
                </td>
                <td className="px-4 py-3 text-right font-mono-num">{u.approval_limit != null ? u.approval_limit.toLocaleString() : "—"}</td>
                <td className="px-4 py-3 text-right whitespace-nowrap"><UserEdit u={u} roleOpts={roleOpts} sites={sites} onDone={load} /><ViewAs u={u} /><button onClick={() => toggle(u)} data-testid={`user-toggle-${u.email}`} className="text-xs text-muted-foreground hover:text-primary">{u.active ? "Disable" : "Enable"}</button><SuperDelete entity="user" id={u.id} label={u.name} onDone={load} className="ml-3 h-7" /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

function Locations() {
  const [rows, setRows] = useState([]);
  const load = useCallback(() => api.get("/locations").then((r) => setRows(r.data)), []);
  useEffect(() => { load(); }, [load]);
  const save = async (l) => { await api.patch(`/locations/${l.id}`, { radius_m: l.radius_m, lat: l.lat, lng: l.lng }); toast.success("Geofence updated"); };
  return (
    <div className="mt-4 grid gap-4 md:grid-cols-2">
      {rows.map((l, idx) => (
        <div key={l.id} className="card-industrial p-4" data-testid={`location-${l.name}`}>
          <div className="flex items-center gap-2 mb-3"><MapPin className="w-4 h-4 text-primary" /><span className="font-heading font-bold flex-1">{l.name}</span>
            <SuperEdit entity="location" record={l} label="site" onDone={load} /><SuperDelete entity="location" id={l.id} label={`site ${l.name}`} onDone={load} /></div>
          <div className="grid grid-cols-3 gap-2">
            {["lat", "lng", "radius_m"].map((k) => (
              <div key={k}><Label className="text-[11px]">{k === "radius_m" ? "Radius (m)" : k}</Label>
                <Input type="number" value={l[k]} data-testid={`loc-${l.name}-${k}`} className="mt-1"
                  onChange={(e) => setRows((s) => s.map((x, i) => (i === idx ? { ...x, [k]: e.target.value } : x)))} /></div>
            ))}
          </div>
          <Button size="sm" className="mt-3" onClick={() => save(l)} data-testid={`loc-save-${l.name}`}>Save geofence</Button>
        </div>
      ))}
    </div>
  );
}

function TaskListEditor() {
  const [list, setList] = useState([]);
  const [name, setName] = useState("");
  useEffect(() => { api.get("/settings").then((r) => setList(r.data.task_list || [])); }, []);
  const save = async (next) => {
    try { const { data } = await api.patch("/settings", { task_list: next }); setList(data.task_list); toast.success("Task list saved"); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const move = (i, d) => { const n = [...list]; [n[i], n[i + d]] = [n[i + d], n[i]]; save(n); };
  const rename = (i) => { const v = window.prompt("Rename task (existing jobs keep the old name)", list[i]); if (v && v.trim()) save(list.map((x, j) => (j === i ? v.trim() : x))); };
  return (
    <div className="mt-4 max-w-xl">
      <p className="text-sm text-muted-foreground mb-3">The office ticks the tasks each job needs. Workers must pick one of the job's tasks when booking time or starting a timer.</p>
      <div className="flex gap-2 mb-3"><Input placeholder="New task" value={name} onChange={(e) => setName(e.target.value)} data-testid="tasklist-new" />
        <Button onClick={() => { save([...list, name.trim()]); setName(""); }} disabled={!name.trim()} data-testid="tasklist-add"><Plus className="w-4 h-4 mr-1" />Add</Button></div>
      <div className="card-industrial divide-y divide-border" data-testid="tasklist">
        {list.map((t, i) => (
          <div key={t} className="px-4 py-2 flex items-center gap-2" data-testid={`tasklist-row-${i}`}>
            <span className="font-mono-num text-xs text-muted-foreground w-6">{i + 1}.</span><span className="flex-1 font-semibold">{t}</span>
            <Button size="sm" variant="ghost" disabled={i === 0} onClick={() => move(i, -1)} data-testid={`tasklist-up-${i}`}>↑</Button>
            <Button size="sm" variant="ghost" disabled={i === list.length - 1} onClick={() => move(i, 1)} data-testid={`tasklist-down-${i}`}>↓</Button>
            <Button size="sm" variant="ghost" onClick={() => rename(i)} data-testid={`tasklist-rename-${i}`}>Rename</Button>
            <Button size="sm" variant="ghost" onClick={() => save(list.filter((_, j) => j !== i))} disabled={list.length === 1} data-testid={`tasklist-delete-${i}`}><Trash2 className="w-4 h-4 text-red-400" /></Button>
          </div>
        ))}
      </div>
    </div>
  );
}

function Templates() {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [stages, setStages] = useState("Cutting, Fabrication, Welding, Finishing, Inspection, Delivery");
  const load = useCallback(() => api.get("/stage-templates").then((r) => setRows(r.data)), []);
  useEffect(() => { load(); }, [load]);
  const create = async () => {
    const st = stages.split(",").map((s, i) => ({ name: s.trim(), sequence: i })).filter((s) => s.name);
    await api.post("/stage-templates", { name, stages: st }); toast.success("Template saved"); setOpen(false); setName(""); load();
  };
  return (
    <>
      <div className="flex justify-end my-4">
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button data-testid="new-template-button" className="font-bold uppercase"><Plus className="w-4 h-4 mr-1" />New Template</Button></DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle className="font-heading uppercase">Stage Template</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label className="text-xs">Name</Label><Input value={name} onChange={(e) => setName(e.target.value)} data-testid="template-name" className="mt-1" /></div>
              <div><Label className="text-xs">Stages (comma-separated, in order)</Label><Input value={stages} onChange={(e) => setStages(e.target.value)} data-testid="template-stages" className="mt-1" /></div>
            </div>
            <DialogFooter><Button onClick={create} disabled={!name} data-testid="template-create-submit" className="font-bold uppercase">Save</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
      <div className="grid gap-3 md:grid-cols-2">
        {rows.map((t) => (
          <div key={t.id} className="card-industrial p-4" data-testid={`template-${t.name}`}>
            <div className="font-heading font-bold mb-2">{t.name}</div>
            <div className="flex flex-wrap gap-1.5">
              {t.stages.map((s, i) => <span key={i} className="text-xs px-2 py-1 rounded bg-accent text-muted-foreground">{i + 1}. {s.name}</span>)}
            </div>
          </div>
        ))}
      </div>
    </>
  );
}

function JobTypes() {
  const [rows, setRows] = useState([]);
  const [name, setName] = useState("");
  const load = useCallback(() => api.get("/job-types").then((r) => setRows(r.data)), []);
  useEffect(() => { load(); }, [load]);
  const add = async () => { if (!name) return; await api.post("/job-types", { name }); setName(""); load(); };
  const del = async (id) => { await api.delete(`/job-types/${id}`); load(); };
  return (
    <div className="mt-4 max-w-md">
      <div className="flex gap-2 mb-4">
        <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="New job type" data-testid="jobtype-input" />
        <Button onClick={add} data-testid="jobtype-add"><Plus className="w-4 h-4" /></Button>
      </div>
      <div className="card-industrial divide-y divide-border">
        {rows.map((t) => (
          <div key={t.id} className="flex items-center justify-between px-4 py-2.5" data-testid={`jobtype-${t.name}`}>
            <span className={t.active === false ? "line-through text-muted-foreground" : ""}>{t.name}</span>
            <div className="flex items-center gap-2"><SuperEdit entity="job_type" record={t} label="job type" onDone={load} />
            <button onClick={() => del(t.id)} className="text-muted-foreground hover:text-red-400" data-testid={`jobtype-delete-${t.name}`}><Trash2 className="w-4 h-4" /></button></div>
          </div>
        ))}
      </div>
    </div>
  );
}

function ImportData() {
  const kinds = [
    { k: "customers", label: "Customers", cols: "name, contact_person, email, phone, address, notes",
      example: "Example Customer Ltd,Example Contact,accounts@example.com,+353 1 234 5678,\"Unit 1, Main Street, Navan, Co. Meath\"" },
    { k: "staff", label: "Staff", cols: "name, email, role, home_location, cost_rate, approval_limit, password",
      example: "Example Worker,worker@example.com,shop_floor,Rathcairn,32,0,ChangeMe2026!",
      note: "role: shop_floor, supervisor or office (Office Manager) · home_location: Rathcairn or Athboy" },
    { k: "jobs", label: "Jobs", cols: "customer_name, customer_po, job_type, job_name, description, tasks, quantity, due_date, location, quoted_value, estimated_cost, billing_method",
      example: "Example Customer Ltd,PO-12345,Structural Steel,Steel columns - Block B,Fabricate 4no. steel columns,Saw Cutting|Drilling|Welding|Painting,4,2026-12-31,Rathcairn,8500,6000,fixed",
      note: "due_date: YYYY-MM-DD · billing_method: fixed, hourly, per_unit or mixed · tasks (required): names from Setup → Task List, separated by |" },
  ];
  const template = (x) => {
    const csv = x.cols.split(", ").join(",") + "\n" + x.example + "\n";
    const url = URL.createObjectURL(new Blob(["\ufeff" + csv], { type: "text/csv" }));
    const a = document.createElement("a"); a.href = url; a.download = `MK-${x.k}-template.csv`; a.click();
    setTimeout(() => URL.revokeObjectURL(url), 2000);
  };
  const up = async (k, file) => {
    if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    try {
      const { data } = await api.post(`/import/${k}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`${data.created} ${k} imported${data.errors.length ? `, ${data.errors.length} skipped` : ""}`);
      if (data.errors.length) data.errors.slice(0, 3).forEach((e) => toast.error(e));
    } catch (e) { toast.error(apiErr(e)); }
  };
  return (
    <div className="mt-4 grid gap-4 md:grid-cols-3">
      {kinds.map((x) => (
        <div key={x.k} className="card-industrial p-4" data-testid={`import-card-${x.k}`}>
          <div className="font-heading font-bold mb-1">{x.label}</div>
          <p className="text-[11px] text-muted-foreground mb-1">CSV columns: {x.cols}</p>
          {x.note && <p className="text-[11px] text-muted-foreground mb-1">{x.note}</p>}
          <p className="text-[11px] text-amber-400/80 mb-3">Delete the example row before uploading.</p>
          <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={() => template(x)} data-testid={`template-${x.k}`} className="font-bold text-sm uppercase"><Download className="w-4 h-4 mr-1" />Template</Button>
          <label className="inline-flex cursor-pointer">
            <input type="file" accept=".csv" className="hidden" data-testid={`import-${x.k}`} onChange={(e) => up(x.k, e.target.files?.[0])} />
            <span className="inline-flex items-center gap-1 px-4 py-2 rounded-lg bg-primary text-primary-foreground font-bold text-sm uppercase"><Upload className="w-4 h-4" />Upload CSV</span>
          </label>
          </div>
        </div>
      ))}
    </div>
  );
}

const OT_FIELDS = [
  { k: "ot_weekly_threshold", label: "Weekly hours before overtime", hint: "Mon–Fri hours above this are paid at the overtime rate" },
  { k: "ot_daily_threshold", label: "Daily hours before overtime", hint: "0 = off. Mon–Fri hours above this per day count as overtime" },
  { k: "ot_multiplier", label: "Overtime rate (x pay)", hint: "e.g. 1.5 = time and a half" },
  { k: "saturday_multiplier", label: "Saturday rate (x pay)", hint: "All Saturday hours" },
  { k: "sunday_multiplier", label: "Sunday rate (x pay)", hint: "All Sunday hours" },
];

function Overtime() {
  const [s, setS] = useState(null);
  useEffect(() => { api.get("/settings").then((r) => setS(r.data)); }, []);
  const save = async () => {
    try { const { data } = await api.patch("/settings", s); setS(data); toast.success("Overtime & reminder rules saved"); }
    catch (e) { toast.error(apiErr(e)); }
  };
  if (!s) return null;
  return (
    <div className="mt-4 grid gap-4 md:grid-cols-2 max-w-4xl">
      <div className="card-industrial p-5 space-y-4" data-testid="overtime-card">
        <div className="font-heading font-bold uppercase text-sm">Overtime rules (weekly payroll)</div>
        {OT_FIELDS.map((x) => (
          <div key={x.k}><Label className="text-xs">{x.label}</Label>
            <Input type="number" step="0.1" value={s[x.k]} onChange={(e) => setS({ ...s, [x.k]: e.target.value })} data-testid={`ot-${x.k}`} className="mt-1" />
            <p className="text-[11px] text-muted-foreground mt-1">{x.hint}</p></div>
        ))}
        <p className="text-[11px] text-muted-foreground">Pay rate per person is set in People (Pay €/hr). If blank, the labour cost rate is used.</p>
      </div>
      <div className="card-industrial p-5 space-y-4 h-fit" data-testid="reminder-card">
        <div className="font-heading font-bold uppercase text-sm">Timer reminders (email)</div>
        <div><Label className="text-xs">Email worker when a timer runs longer than (hours)</Label>
          <Input type="number" step="0.5" value={s.timer_reminder_hours} onChange={(e) => setS({ ...s, timer_reminder_hours: e.target.value })} data-testid="ot-timer_reminder_hours" className="mt-1" /></div>
        <div><Label className="text-xs">Also copy office manager (optional email)</Label>
          <Input type="email" value={s.reminder_cc_email || ""} onChange={(e) => setS({ ...s, reminder_cc_email: e.target.value })} placeholder="office@yourcompany.ie" data-testid="ot-reminder_cc_email" className="mt-1" /></div>
        <p className="text-[11px] text-muted-foreground">Checked every 15 minutes. One reminder per timer.</p>
        <div className="pt-3 border-t border-border font-heading font-bold uppercase text-sm">Leave emails</div>
        <div><Label className="text-xs">Send new leave requests and withdrawals to</Label>
          <Input type="email" value={s.leave_notify_email ?? ""} onChange={(e) => setS({ ...s, leave_notify_email: e.target.value })} placeholder="accounts@mkeng.ie" data-testid="ot-leave_notify_email" className="mt-1" /></div>
        <p className="text-[11px] text-muted-foreground">Workers are emailed at their login address when leave is approved, rejected or added for them.</p>
      </div>
      <Button onClick={save} data-testid="overtime-save" className="font-bold uppercase w-fit">Save</Button>
    </div>
  );
}

function Rules() {
  const [s, setS] = useState(null);
  useEffect(() => { api.get("/settings").then((r) => setS(r.data)); }, []);
  const save = async () => { await api.patch("/settings", s); toast.success("Rules saved"); };
  if (!s) return null;
  return (
    <div className="mt-4 max-w-md card-industrial p-5 space-y-4">
      <div><Label className="text-xs">Default labour cost €/hr</Label><Input type="number" value={s.default_labour_rate} onChange={(e) => setS({ ...s, default_labour_rate: e.target.value })} data-testid="rule-labour" className="mt-1" /></div>
      <div><Label className="text-xs">VAT rate %</Label><Input type="number" value={s.vat_rate} onChange={(e) => setS({ ...s, vat_rate: e.target.value })} data-testid="rule-vat" className="mt-1" /></div>
      <Button onClick={save} data-testid="rules-save" className="font-bold uppercase">Save</Button>
      <p className="text-[11px] text-muted-foreground">These defaults are configurable. Per-person labour rates are set on each person.</p>
    </div>
  );
}
